import { ref, type Ref } from 'vue';
import orderService, { type CreateOrderData, type OrderItem } from '@/services/orderService';
import specialNotesService from '@/services/specialNotesService';
import { mapOrderTypeToBackend } from '@/utils/orderFormHelpers';
import type { OrderPerson } from './useMultipleDiners';
import { usePromotions } from '@/composables/usePromotions';

/**
 * Composable para gestionar la creación y actualización de órdenes
 * Separa la lógica compleja de creación/edición del componente principal
 */
export function useOrderCreation() {
  const { calculatePromotionsForOrder } = usePromotions();

  /**
   * Calcular promociones para una orden y aplicar descuentos
   */
  async function calculateOrderPromotions(
    items: OrderItem[],
    orderTotal: number
  ): Promise<{ discountAmount: number; appliedPromotions: any[] }> {
    try {
      const promotionResult = await calculatePromotionsForOrder(
        items,
        new Date().toISOString(),
        orderTotal
      );
      
      return {
        discountAmount: promotionResult.total_discount,
        appliedPromotions: promotionResult.applied_promotions
      };
    } catch (error) {
      return { discountAmount: 0, appliedPromotions: [] };
    }
  }

  /**
   * Valida que la orden tenga items
   */
  function validateHasItems(
    isMultipleDinersMode: boolean,
    persons: OrderPerson[],
    validItems: any[]
  ): boolean {
    if (isMultipleDinersMode) {
      const totalItems = persons.reduce((sum, person) => sum + person.items.length, 0);
      return totalItems > 0;
    }
    return validItems.length > 0;
  }

  /**
   * Construye los updates para una orden existente
   */
  function buildOrderUpdates(
    form: any,
    existingOrder: any,
    ivaEnabled?: boolean,
    ivaRate?: number,
    selectedCustomer?: any,
    selectedAddress?: any,
    deliveryFee?: number
  ): any {
    const updates: any = {};
    
    // Table ID
    const newTableId = form.type === 'Dine-in' ? (form.tableId || null) : null;
    if (newTableId !== existingOrder.table_id) {
      updates.table_id = newTableId;
    }
    
    // Notes
    if (form.notes !== (existingOrder.notes || '')) {
      updates.notes = form.notes || null;
    }
    
    // Customer name
    const newCustomerName = form.type !== 'Dine-in' ? (form.customerName || null) : null;
    if (newCustomerName !== (existingOrder.customer_name || null)) {
      updates.customer_name = newCustomerName;
    }
    
    // Order type - always send to ensure backend has correct type for delivery fee calculation
    const newOrderType = mapOrderTypeToBackend(form.type);
    updates.order_type = newOrderType;
    
    // Customer and address data for delivery/takeaway orders
    if (form.type === 'Delivery' || form.type === 'Takeaway') {
      if (selectedCustomer) {
        // Check if customer changed
        const currentCustomerId = (existingOrder as any).customer_id;
        if (!currentCustomerId || selectedCustomer.id !== currentCustomerId) {
          updates.customer_id = selectedCustomer.id;
        }
        
        // For delivery orders, always send the selected address to ensure consistency
        if (form.type === 'Delivery' && selectedAddress) {
          // Always send address_id for delivery orders to ensure backend has correct address
          updates.address_id = selectedAddress.id;
        }
        
        // For updates, NEVER send customer_data - only send customer_id and address_id
        // customer_data should only be sent when creating new orders, not updating existing ones
      }
    }
    
    // IVA fields - compare with existing values
    const currentIvaEnabled = (existingOrder as any).iva_enabled || false;
    const currentIvaRate = (existingOrder as any).iva_rate || 0.16;
    
    // Update IVA if values are provided and different
    if (ivaEnabled !== undefined && ivaEnabled !== currentIvaEnabled) {
      updates.iva_enabled = ivaEnabled;
    }
    
    if (ivaRate !== undefined && Math.abs(ivaRate - currentIvaRate) > 0.001) {
      updates.iva_rate = ivaRate;
    }
    
    // Delivery fee - no longer sent from frontend as backend recalculates it automatically
    // based on subtotal, discounts, and restaurant rules
    
    return updates;
  }

  /**
   * Actualiza los items de una orden existente (upsert + delete)
   * Sincroniza los items de la orden con los items del formulario:
   * - Actualiza items existentes que están en el formulario
   * - Agrega items nuevos del formulario
   * - Elimina items que ya no están en el formulario
   */
  async function updateOrderItems(
    orderId: number,
    validItems: any[],
    existingItems: OrderItem[]
  ): Promise<void> {
    // Build lookup map
    const existingMap = new Map<string, OrderItem>();
    for (const it of existingItems) {
      const key = `${it.menu_item_id}|${it.variant_id ?? ''}`;
      existingMap.set(key, it);
    }

    // Track seen items
    const seenKeys = new Set<string>();

    // Upserts: update existing or add new
    for (const item of validItems) {
      const key = `${item.menu_item_id}|${item.variant_id ?? ''}`;
      const existing = existingMap.get(key);
      
      if (existing) {
        // Update existing item
        const patch: any = {};
        if (existing.quantity !== item.quantity) patch.quantity = item.quantity;
        
        const desiredUnitPrice = item.unit_price ?? existing.unit_price ?? 0;
        if ((existing.unit_price ?? 0) !== desiredUnitPrice) {
          patch.unit_price = desiredUnitPrice;
        }
        
        const si = item.special_instructions ?? null;
        if ((existing.special_instructions ?? null) !== si) {
          patch.special_instructions = si;
        }
        
        const desiredVariantId = item.variant_id ?? null;
        if ((existing.variant_id ?? null) !== desiredVariantId) {
          patch.variant_id = desiredVariantId;
        }
        
        if (Object.keys(patch).length > 0) {
          await orderService.updateOrderItem(orderId, existing.id, patch);
        }
        seenKeys.add(key);
      } else {
        // Add new item
        await orderService.addOrderItem(orderId, {
          menu_item_id: item.menu_item_id,
          variant_id: item.variant_id ?? null,
          quantity: item.quantity,
          special_instructions: item.special_instructions ?? null,
          unit_price: item.unit_price ?? 0,
          person_id: item.person_id ?? null,  // Include person_id for new items
        });
      }
    }

    // Deletions: remove items no longer present in the form
    for (const [key, ex] of existingMap.entries()) {
      if (!validItems.some(it => `${it.menu_item_id}|${(it.variant_id ?? '')}` === key)) {
        await orderService.deleteOrderItem(orderId, ex.id);
      }
    }
  }

  /**
   * Construye el payload para crear una nueva orden
   */
  function buildOrderPayload(
    form: any,
    isMultipleDinersMode: boolean,
    persons: OrderPerson[],
    validItems: any[],
    ivaEnabled?: boolean,
    ivaRate?: number,
    deliveryFee?: number
  ): CreateOrderData {
    const basePayload: any = {
      table_id: form.type === 'Dine-in' ? (form.tableId as number | null) : null,
      customer_name: form.type !== 'Dine-in' ? (form.customerName || null) : null,
      order_type: mapOrderTypeToBackend(form.type),
      notes: form.notes || null,
      iva_enabled: ivaEnabled || false,
      iva_rate: ivaRate || 0.16,
      delivery_fee: deliveryFee || 0,
    };

    // Preserve customer_data for delivery and takeaway orders
    if (form.customer_data && (form.type === 'Delivery' || form.type === 'Takeaway')) {
      basePayload.customer_data = form.customer_data;
    }

    if (isMultipleDinersMode) {
      // Multiple diners format
      return {
        ...basePayload,
        persons: persons.map(person => ({
          name: person.name || null,
          position: person.position,
          items: person.items.map(it => ({
            menu_item_id: Number(it.menu_item_id),
            variant_id: it.variant_id ? Number(it.variant_id) : null,
            quantity: it.quantity,
            special_instructions: it.special_instructions ?? null,
            unit_price: it.unit_price ?? 0,
            item_type: it.item_type || 'dine_in',
            extras: it.extras || [],
          }))
        }))
      };
    } else {
      // Legacy format
      return {
        ...basePayload,
        items: validItems.map(it => ({
          menu_item_id: Number(it.menu_item_id),
          variant_id: it.variant_id ? Number(it.variant_id) : null,
          quantity: it.quantity,
          special_instructions: it.special_instructions ?? null,
          unit_price: it.unit_price ?? 0,
          extras: it.extras || [],
        })),
      };
    }
  }

  /**
   * Maneja el pago de una orden si está marcada como pagada
   */
  async function handleOrderPayment(
    orderId: number,
    markAsPaid: boolean,
    paymentMethod: 'cash' | 'card' | 'digital' | 'other',
    cashReceived: number,
    orderTotal: number,
    t: (key: string) => string,
    showSuccess: (msg: string) => void,
    showWarning: (msg: string, duration?: number) => void,
    showError: (msg: string) => void,
    suppressSuccessMessage: boolean = false
  ): Promise<boolean> {
    if (!markAsPaid) {
      if (!suppressSuccessMessage) {
        showSuccess(t('app.views.orders.messages.order_created_success'));
      }
      return true;
    }

    // Validate cash payment has sufficient amount
    if (paymentMethod === 'cash') {
      if (!cashReceived || cashReceived <= 0) {
        showError(t('app.views.orders.payment.enter_amount') || 'Por favor ingresa el monto recibido');
        return false;
      }
      
      if (cashReceived < orderTotal) {
        showError(t('app.views.orders.payment.insufficient_amount') || 'El monto recibido es insuficiente');
        return false;
      }
    }

    try {
      await orderService.markOrderPaid(orderId, paymentMethod);
      if (!suppressSuccessMessage) {
        showSuccess(t('app.views.orders.messages.order_created_and_paid_success') || 'Order created and marked as paid successfully');
      }
      return true;
    } catch (paymentError: any) {
      
      const errorDetail = paymentError.response?.data?.detail || '';
      const errorMessage = paymentError.response?.data?.error?.message || '';
      
      if (errorDetail.includes('No open cash register session') || 
          errorDetail.includes('cash register session') ||
          errorMessage.includes('No open cash register session') ||
          errorMessage.includes('cash register session')) {
        showWarning('La orden se creó correctamente pero no se pudo marcar como pagada. No hay una sesión de caja abierta. Por favor abre una sesión de caja y marca la orden como pagada manualmente.', 8000);
      } else {
        showWarning('La orden se creó correctamente pero no se pudo procesar el pago. Por favor marca la orden como pagada manualmente desde los detalles de la orden.', 8000);
      }
      return false;
    }
  }

  /**
   * Actualiza una orden existente
   */
  async function updateExistingOrder(
    orderId: number,
    form: any,
    existingOrder: any,
    validItems: any[],
    persons: any[],
    ivaEnabled?: boolean,
    ivaRate?: number,
    selectedCustomer?: any,
    selectedAddress?: any,
    deliveryFee?: number,
    markAsPaid?: boolean,
    paymentMethod?: 'cash' | 'card' | 'digital' | 'other',
    cashReceived?: number,
    currentDiscount?: number,
    appliedPromotions?: any[]
  ): Promise<any> {
    // Build complete order update including items using new backend support
    const updates = buildOrderUpdates(form, existingOrder, ivaEnabled, ivaRate, selectedCustomer, selectedAddress, deliveryFee);
    
    // Add persons to the update payload for multi-diner support
    // Note: persons already contains their items, no need to send separate items array
    updates.persons = persons.map(person => ({
      id: person.id,
      name: person.name || null,
      position: person.position,
      items: person.items.map(item => ({
        menu_item_id: item.menu_item_id,
        variant_id: item.variant_id ?? null,
        quantity: item.quantity,
        special_instructions: item.special_instructions ?? null,
        unit_price: item.unit_price || 0
        // Note: person_id will be assigned by backend based on the person
      }))
    }));

    // Add promotions if provided
    if (currentDiscount !== undefined && appliedPromotions !== undefined) {
      updates.applied_promotions = appliedPromotions;
      updates.promotion_discount = currentDiscount;
    }

    // Single API call to update order and items together
    const updatedOrder = await orderService.updateOrder(orderId, updates);

    // Handle payment if markAsPaid is true and order is not already paid
    if (markAsPaid && !existingOrder.is_paid) {
      // Validate cash payment if needed
      if (paymentMethod === 'cash') {
        if (!cashReceived || cashReceived <= 0) {
          throw new Error('Por favor ingresa el monto recibido');
        }
        
        // Calculate total for validation
        const subtotal = validItems.reduce((sum, item) => {
          return sum + ((item.unit_price || 0) * item.quantity);
        }, 0);
        const deliveryFeeAmount = deliveryFee || 0;
        const ivaAmount = ivaEnabled ? ((subtotal + deliveryFeeAmount) * (ivaRate || 0.16)) : 0;
        const total = subtotal + deliveryFeeAmount + ivaAmount;
        
        if (cashReceived < total) {
          throw new Error('El monto recibido es insuficiente');
        }
      }
      
      // Mark order as paid
      await orderService.markOrderPaid(orderId, paymentMethod || 'cash', existingOrder.status);
    }

    // Get updated order
    return await orderService.getOrder(orderId);
  }

  /**
   * Crea una nueva orden
   */
  async function createNewOrder(
    form: any,
    isMultipleDinersMode: boolean,
    persons: OrderPerson[],
    validItems: any[],
    markAsPaid: boolean,
    paymentMethod: 'cash' | 'card' | 'digital' | 'other',
    cashReceived: number,
    orderTotal: number,
    t: (key: string) => string,
    showSuccess: (msg: string) => void,
    showWarning: (msg: string, duration?: number) => void,
    showError: (msg: string) => void,
    suppressSuccessMessage: boolean = false,
    ivaEnabled?: boolean,
    ivaRate?: number,
    deliveryFee?: number,
    currentDiscount?: number,
    appliedPromotions?: any[]
  ): Promise<{ order: any; paymentSuccess: boolean; discountAmount: number }> {
    // Validate cash payment BEFORE creating the order
    if (markAsPaid && paymentMethod === 'cash') {
      if (!cashReceived || cashReceived <= 0) {
        showError(t('app.views.orders.payment.enter_amount') || 'Por favor ingresa el monto recibido');
        throw new Error('VALIDATION_ERROR'); // Prevent order creation
      }
      
      if (cashReceived < orderTotal) {
        showError(t('app.views.orders.payment.insufficient_amount') || 'El monto recibido es insuficiente');
        throw new Error('VALIDATION_ERROR'); // Prevent order creation
      }
    }

    // Build payload
    const orderPayload = buildOrderPayload(form, isMultipleDinersMode, persons, validItems, ivaEnabled, ivaRate, deliveryFee);

    // Use provided discount and promotions, or calculate if not provided
    let discountAmount = currentDiscount || 0;
    let promotionsToApply = appliedPromotions || [];
    
    // Calculate promotions only if not provided
    if (currentDiscount === undefined || appliedPromotions === undefined) {
      const orderItems = validItems.map(item => ({
        menu_item_id: item.menu_item_id,
        variant_id: item.variant_id || undefined,
        quantity: item.quantity,
        unit_price: item.unit_price || 0,
        item_type: item.item_type || 'dine_in'
      })) as OrderItem[];
      
      const calculated = await calculateOrderPromotions(orderItems, orderTotal);
      discountAmount = calculated.discountAmount;
      promotionsToApply = calculated.appliedPromotions;
    }
    
    // Create enhanced payload with promotions
    const enhancedOrderPayload = {
      ...orderPayload,
      applied_promotions: promotionsToApply,
      promotion_discount: discountAmount
    };

    // Create order
    const created = await orderService.createOrder(enhancedOrderPayload);

    // Handle payment (validation already done above)
    const finalOrderTotal = orderTotal - discountAmount;
    const paymentSuccess = await handleOrderPayment(
      created.id,
      markAsPaid,
      paymentMethod,
      cashReceived,
      finalOrderTotal,
      t,
      showSuccess,
      showWarning,
      showError,
      suppressSuccessMessage
    );

    return { order: created, paymentSuccess, discountAmount };
  }

  return {
    validateHasItems,
    buildOrderUpdates,
    updateOrderItems,
    buildOrderPayload,
    handleOrderPayment,
    updateExistingOrder,
    createNewOrder
  };
}
